import React, { useState } from 'react';
import { formatCurrency, formatDate } from '../utils/helpers';
import ReferenceTag from './ReferenceTag';
import NoticeClaimChip from './NoticeClaimChip';
import { noticeHeldByOther, noticeLockedReason } from '../utils/noticeClaim';
import './CreatePRModal.css';

// ===========================================================================
// "IS THIS THE SAME PAYMENT?" — the duplicate-PR fail-safe.
//
// Finance sometimes records an incoming credit the moment they see it on the
// bank statement; it then sits UNCLAIMED. Sales, who cannot see that, is about
// to submit a proof for the same money — which would ask finance to record a PR
// that already exists. Whenever the amount sales is submitting matches an
// unclaimed credit, this stands between them and the submit button.
//
// ONE component for both surfaces (the generic Payment Received modal and the
// P2P Sell platform order) so the wording, the evidence shown, and the two
// choices can never drift apart. The caller supplies what "link" means:
//   onLink(credit)  — claim this credit (no task raised)
//   onSkip()        — carry on with the normal submission for finance to claim
//
// Deliberately NOT auto-resolving even when there is exactly one match: only
// the human knows whether their customer's transfer IS that line on the
// statement. Same-amount coincidences are ordinary in this business.
//
// TWO MODES, because the same list answers two different questions:
//   'failsafe' (default) — WE interrupted THEM: "you're about to submit a proof
//                          for money that may already be recorded."
//   'picker'             — THEY asked US: they clicked "Link the existing PR",
//                          so there is nothing to warn about; just show the
//                          matches and let them choose or back out.
// ===========================================================================
function MatchingCreditPrompt({
  isOpen,
  credits,
  amount,
  busy = false,
  mode = 'failsafe',
  skipLabel,
  linkLabel = 'Yes — link it for me',
  onLink,
  onSkip,
  onClose,
  // The viewer — a credit whose notice SOMEONE ELSE holds (utils/noticeClaim)
  // is shown with who holds it and cannot be picked: the server refuses the
  // link (409 NOTICE_CLAIMED).
  user = null,
}) {
  const isPicker = mode === 'picker';
  const resolvedSkipLabel = skipLabel || (isPicker ? 'Cancel' : 'No — submit my proof for finance to claim');
  const list = Array.isArray(credits) ? credits : [];
  // Preselect the single match so one click finishes the job; with several,
  // nothing is selected until the operator says which one. DERIVED, not seeded
  // in useState: the component mounts before the matches arrive (parents render
  // it unconditionally), so an initializer would run against an empty list and
  // the single-match preselect would never apply. Match sets are amount-
  // disjoint, so a stale click can never carry over into a new list.
  const [selectedId, setSelectedId] = useState(null);

  if (!isOpen || list.length === 0) return null;

  const one = list.length === 1;
  const pickable = (c) => !noticeHeldByOther(c, user);
  const effectiveId = selectedId != null ? selectedId : (one && pickable(list[0]) ? list[0].id : null);
  const selected = list.find((c) => c.id === effectiveId) || null;
  const amt = formatCurrency(amount != null ? amount : (list[0] && list[0].amount), 'MYR');

  return (
    <div className="modal-overlay" style={{ zIndex: 1200 }}>
      <div className="modal-content create-pr-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 620 }}>
        <div className="modal-header">
          <h3>
            {isPicker
              ? (one ? 'One unclaimed PR matches this amount' : `${list.length} unclaimed PRs match this amount`)
              : (one ? 'This payment may already be recorded' : 'These payments may already be recorded')}
          </h3>
          {onClose && (
            <button onClick={onClose} className="close-button" disabled={busy}>&times;</button>
          )}
        </div>

        <div className="modal-body">
          <p style={{ marginTop: 0, color: '#57606a', fontSize: 14 }}>
            {isPicker ? (
              one ? (
                <>
                  Finance recorded <strong>{amt}</strong> from{' '}
                  <strong>&ldquo;{list[0].description || 'unnamed payer'}&rdquo;</strong> and nobody has claimed it.
                  Link it to this order?
                </>
              ) : (
                <>
                  Finance recorded {list.length} unclaimed payments of <strong>{amt}</strong>. Pick the one
                  that belongs to this order.
                </>
              )
            ) : one ? (
              <>
                An unclaimed PR of <strong>{amt}</strong> from{' '}
                <strong>&ldquo;{list[0].description || 'unnamed payer'}&rdquo;</strong> (recorded by finance)
                matches the amount you&rsquo;re submitting. Is this the same payment?
              </>
            ) : (
              <>
                {list.length} unclaimed PRs of <strong>{amt}</strong> (recorded by finance) match the amount
                you&rsquo;re submitting. If one of them is your payment, pick it — otherwise carry on.
              </>
            )}
          </p>

          <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
            {list.map((c, i) => {
              const active = c.id === effectiveId;
              const locked = !pickable(c);
              return (
                <div
                  key={c.id}
                  onClick={() => !busy && !locked && setSelectedId(c.id)}
                  title={locked ? noticeLockedReason(c) : undefined}
                  style={{
                    padding: '10px 12px',
                    cursor: busy || locked ? 'not-allowed' : 'pointer',
                    borderTop: i === 0 ? 'none' : '1px solid #f1f5f9',
                    background: locked ? '#f3f4f6' : (active ? '#eff6ff' : '#fff'),
                    color: locked ? '#6b7280' : undefined,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                    <strong style={{ wordBreak: 'break-word' }}>{c.description || 'No sender name recorded'}</strong>
                    <span style={{ whiteSpace: 'nowrap' }}>{formatCurrency(c.amount, 'MYR')}</span>
                  </div>
                  <div style={{ color: '#57606a', fontSize: 12, marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span>{c.account_label}</span>
                    <span>·</span>
                    <span>{formatDate(c.created_at)}</span>
                    <ReferenceTag value={c.reference_no} />
                    <NoticeClaimChip row={c} user={user} style={{ marginTop: 0 }} />
                  </div>
                </div>
              );
            })}
          </div>

          <small className="form-text" style={{ display: 'block', marginBottom: 12 }}>
            Linking claims the money finance already recorded — nothing is booked twice and no new task is
            raised.
            {!isPicker && ' If it is a different payment that happens to be the same amount, choose the second option.'}
          </small>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || !selected}
              onClick={() => selected && onLink(selected)}
            >
              {busy ? 'Linking…' : linkLabel}
            </button>
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={onSkip}>
              {resolvedSkipLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MatchingCreditPrompt;
